import { ChangeEvent, SyntheticEvent } from "react";
import Autocomplete, { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { SystemSetting } from "../../../types/system-setting.type";
import CustomerGroupCodeInput from "./CustomerGroupCodeInput";
import DurationMinutesInput from "./DurationMinutesInput";
import PricebookCodeInput from "./PricebookCodeInput";
import { isDurationMinutesSetting, settingOptionLabel } from "./settingMeta";
import { SettingDraft, normalizeListItems } from "./settingValue";

const NO_LIST_OPTIONS: string[] = [];
const NO_CHOICE_OPTIONS: string[] = [];
const LIST_INPUT_HINT = "พิมพ์แล้วกด Enter เพื่อเพิ่มทีละค่า";
const NUMBER_INPUT_SLOT_PROPS = { htmlInput: { inputMode: "decimal" } } as const;

interface SettingValueInputProps {
  setting: SystemSetting;
  draft: SettingDraft;
  error: string | null;
  /** The value's rules, shown while there is no error to show instead. */
  helperText: string;
  disabled: boolean;
  onChange: (draft: SettingDraft) => void;
}

/** The input that fits the setting's value type. */
export default function SettingValueInput({
  setting,
  draft,
  error,
  helperText,
  disabled,
  onChange,
}: Readonly<SettingValueInputProps>) {
  const handleTextChange = (text: string) => onChange({ ...draft, text });

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    handleTextChange(event.target.value);

  const handleItemsChange = (_event: SyntheticEvent, items: string[]) =>
    onChange({ ...draft, items: normalizeListItems(items) });

  const renderListInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      size="small"
      placeholder={LIST_INPUT_HINT}
      error={Boolean(error)}
      helperText={error ?? helperText}
    />
  );

  if (isDurationMinutesSetting(setting.key)) {
    return (
      <DurationMinutesInput
        value={draft.text}
        error={error}
        helperText={helperText}
        disabled={disabled}
        onChange={handleTextChange}
      />
    );
  }

  switch (setting.value_type) {
    case "choice":
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={draft.text}
          onChange={handleInputChange}
          error={Boolean(error)}
          helperText={error ?? (helperText || undefined)}
          disabled={disabled}
        >
          {(setting.constraints.options ?? NO_CHOICE_OPTIONS).map((option) => (
            <MenuItem key={option} value={option}>
              {settingOptionLabel(setting.key, option)}
            </MenuItem>
          ))}
        </TextField>
      );
    case "pricebook_code":
      return (
        <PricebookCodeInput
          value={draft.text}
          error={error}
          helperText={helperText}
          disabled={disabled}
          onChange={handleTextChange}
        />
      );
    case "customer_group_code":
      return (
        <CustomerGroupCodeInput
          value={draft.text}
          savedCode={String(setting.value)}
          error={error}
          helperText={helperText}
          disabled={disabled}
          onChange={handleTextChange}
        />
      );
    case "text":
      return (
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={3}
          value={draft.text}
          onChange={handleInputChange}
          error={Boolean(error)}
          helperText={error ?? helperText}
          disabled={disabled}
        />
      );
    case "integer_list":
    case "text_list":
      // autoSelect keeps a value that was typed but not yet confirmed with Enter when the
      // field loses focus, so it is not silently dropped on save.
      return (
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          options={NO_LIST_OPTIONS}
          value={draft.items}
          onChange={handleItemsChange}
          renderInput={renderListInput}
          disabled={disabled}
        />
      );
    default:
      return (
        <TextField
          size="small"
          fullWidth
          value={draft.text}
          onChange={handleInputChange}
          error={Boolean(error)}
          helperText={error ?? helperText}
          disabled={disabled}
          slotProps={NUMBER_INPUT_SLOT_PROPS}
        />
      );
  }
}
